import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, ArrowRight } from 'lucide-react';
import { useHaberler } from '../../hooks/useSupabaseData';
import { useTranslation } from 'react-i18next';

const parseDate = (dateStr) => {
  if (!dateStr) return new Date(0);
  if (dateStr.includes('.')) {
    const [day, month, year] = dateStr.split('.');
    return new Date(year, month - 1, day);
  }
  return new Date(dateStr);
};

const formatDate = (dateStr, lang = 'tr') => {
  try {
    const d = parseDate(dateStr);
    return d.toLocaleDateString(lang === 'en' ? 'en-GB' : 'tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
};

// ── Haber Kartı ───────────────────────────────────────────────────────────────
const NewsCard = ({ item, lang }) => {
  const link = item.link && item.link !== '#' ? item.link : null;
  const isExternal = link?.startsWith('http');
  const dest = !isExternal && link ? link : item.slug ? `/haberler/${item.slug}` : item.id ? `/haberler/${item.id}` : null;

  const Wrapper = ({ children }) => {
    if (!dest) return <div className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full">{children}</div>;
    if (isExternal) return <a href={dest} target="_blank" rel="noopener noreferrer" className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full cursor-pointer">{children}</a>;
    return <Link to={dest} className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col h-full cursor-pointer">{children}</Link>;
  };

  return (
    <Wrapper>

      {/* Fotoğraf + gradient + tarih + başlık overlay */}
      <div className="relative overflow-hidden flex-shrink-0" style={{ aspectRatio: '3/2' }}>
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&h=400&fit=crop'; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-3 pb-3 pt-8">
          <div className="mb-2">
            <p className="text-[10px] font-bold uppercase tracking-wide text-white leading-none mb-1">
              {formatDate(item.created_at, lang)}
            </p>
            <div className="w-6 h-[2px] rounded-full bg-white/50" />
          </div>
          <span className="text-[12px] font-bold text-white group-hover:text-amber-300 transition-colors duration-200 leading-snug line-clamp-2 flex items-start gap-1">
            <span className="flex-1">{item.title}</span>
            {dest && <ArrowRight size={11} className="flex-shrink-0 mt-0.5 opacity-70" />}
          </span>
        </div>
      </div>

      {/* Özet */}
      <div className="px-3 pt-3 pb-3 flex-1">
        <p className="text-[13px] text-gray-700 leading-relaxed line-clamp-2">
          {item.summary}
        </p>
      </div>

    </Wrapper>
  );
};

// ── Ana Bileşen ───────────────────────────────────────────────────────────────
const NewsSection = () => {
  const { t, i18n } = useTranslation();
  const { haberler } = useHaberler();
  const [news, setNews] = useState([]);

  useEffect(() => {
    const sorted = haberler
      .filter(h => h.baslik && h.ozet)
      .map(h => ({
        title: h.baslik,
        summary: h.ozet,
        image: h.resim_url || 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=600&h=450&fit=crop',
        created_at: h.tarih,
        link: h.link,
        slug: h.slug,
        id: h.id,
      }))
      .sort((a, b) => parseDate(b.created_at) - parseDate(a.created_at))
      .slice(0, 8);
    setNews(sorted);
  }, [haberler]);

  if (!news.length) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Başlık satırı */}
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[#7B0D1E] mb-2">{t('home.news.badge')}</p>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight">
              {t('home.news.title')}
            </h2>
          </div>
          <Link
            to="/haberler"
            className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-[#7B0D1E] transition-colors duration-200 pb-1"
          >
            {t('home.news.viewAll')}
            <ChevronRight size={15} />
          </Link>
        </div>

        {/* 4 sütunlu grid — 9 haber */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {news.map((item, i) => (
            <NewsCard key={item.id || i} item={item} lang={i18n.language} />
          ))}
        </div>

      </div>
    </section>
  );
};

export default NewsSection;
